import type { Thread, ThreadDetail, ThreadPriority, ThreadStatus } from '@central/api';
import { charCount } from '@central/ui';
import { Alert, Button, Modal, Select, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { errorMessage } from '../lib/errors';
import { priorityOptions, statusOptions } from '../lib/inbox';
import { useCanWrite, useIsReseller, useMe } from '../lib/me';
import { useThreadsApi } from '../lib/threads';

const assigneeLabel = (id: string, name: string, meId: string) =>
  id === meId ? `${name} (me)` : name;

/** Who a thread can be assigned to: a reseller's own team, or Rethink's staff when listable. */
function useAssignableStaff() {
  const api = useApi();
  const reseller = useIsReseller();
  const canListStaff = useCanWrite();
  // The cache holds each route's own page (other pages read the same keys);
  // select maps it to the people offered.
  return useQuery({
    queryKey: reseller ? keys.partnerTeam : keys.staff,
    queryFn: (): Promise<{ items: Array<{ id: string; name: string; enabled?: boolean }> }> =>
      reseller ? api.partner.team.list() : api.staff.list(),
    select: (page) =>
      page.items.map((u) => ({ id: u.id, name: u.name, enabled: u.enabled ?? true })),
    enabled: reseller || canListStaff,
    staleTime: 60_000,
  });
}

/** Resolve or reopen, status, priority, assignee, the tier move and the PDF reports, stacked. */
export function ThreadControls({ t }: { t: Thread }) {
  const threads = useThreadsApi();
  const qc = useQueryClient();
  const me = useMe();
  const staff = useAssignableStaff();
  const [moving, setMoving] = useState<'escalate' | 'return'>();
  const update = useMutation({
    mutationFn: (b: { status?: ThreadStatus; assigneeId?: string; priority?: ThreadPriority }) =>
      threads.update(t.id, b),
    onSuccess: (next) => {
      qc.setQueryData<InfiniteData<ThreadDetail>>(keys.thread(t.id), (d) =>
        d ? { ...d, pages: d.pages.map((p) => ({ ...p, thread: next })) } : d,
      );
      // A move into or out of closed, and a new assignee, post an event to the timeline.
      if (
        (next.status === 'closed') !== (t.status === 'closed') ||
        next.assignee?.id !== t.assignee?.id
      ) {
        void qc.invalidateQueries({ queryKey: keys.thread(t.id), exact: true });
      }
      void qc.invalidateQueries({ queryKey: [...keys.threads, 'list'] });
      void qc.invalidateQueries({ queryKey: [...keys.threads, 'count'] });
    },
  });

  // Support cannot list staff: offer themselves, the current assignee and unassigned.
  const people = new Map<string, string>();
  people.set(me.user.id, me.user.name);
  for (const s of staff.data ?? []) if (s.enabled) people.set(s.id, s.name);
  if (t.assignee) people.set(t.assignee.id, t.assignee.name);
  const assignees = [
    { value: '', label: 'Unassigned' },
    ...[...people].map(([id, name]) => ({ value: id, label: assigneeLabel(id, name, me.user.id) })),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {t.status === 'closed' ? (
        <Button
          variant="default"
          loading={update.isPending && update.variables?.status === 'waiting_on_us'}
          disabled={update.isPending}
          onClick={() => update.mutate({ status: 'waiting_on_us' })}
        >
          Reopen
        </Button>
      ) : (
        <Button
          color="dark"
          loading={update.isPending && update.variables?.status === 'closed'}
          disabled={update.isPending}
          onClick={() => update.mutate({ status: 'closed' })}
        >
          Resolve
        </Button>
      )}
      <Select
        label="Status"
        size="sm"
        data={statusOptions}
        value={t.status}
        allowDeselect={false}
        disabled={update.isPending}
        onChange={(v) => v && v !== t.status && update.mutate({ status: v as ThreadStatus })}
      />
      <Select
        label="Priority"
        size="sm"
        data={priorityOptions}
        value={t.priority}
        allowDeselect={false}
        disabled={update.isPending}
        onChange={(v) => v && v !== t.priority && update.mutate({ priority: v as ThreadPriority })}
      />
      <Select
        label="Assignee"
        size="sm"
        data={assignees}
        value={t.assignee?.id ?? ''}
        allowDeselect={false}
        disabled={update.isPending}
        onChange={(v) =>
          v !== null && v !== (t.assignee?.id ?? '') && update.mutate({ assigneeId: v })
        }
      />
      {t.tier === 'tier1' ? (
        <Button variant="default" onClick={() => setMoving('escalate')}>
          Escalate to Rethink
        </Button>
      ) : (
        !me.user.reseller &&
        t.escalatedAt && (
          <Button variant="default" onClick={() => setMoving('return')}>
            Return to reseller
          </Button>
        )
      )}
      {update.error && <Alert color="red">{errorMessage(update.error)}</Alert>}
      <Button component="a" variant="default" size="sm" href={threads.reportUrl(t.id)} download>
        Download report (PDF)
      </Button>
      <Button
        component="a"
        variant="subtle"
        size="xs"
        href={threads.reportUrl(t.id, true)}
        download
      >
        Report with internal notes
      </Button>
      {moving && <TierMove t={t} move={moving} onClose={() => setMoving(undefined)} />}
    </div>
  );
}

const MAX_REASON = 2000;

/**
 * Escalate a tier 1 thread to Rethink (a reason is required), or hand an
 * escalated one back to the reseller. The reason is an internal note: the
 * customer never sees it.
 */
function TierMove({
  t,
  move,
  onClose,
}: {
  t: Thread;
  move: 'escalate' | 'return';
  onClose: () => void;
}) {
  const api = useApi();
  const reseller = useIsReseller();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string>();
  const run = useMutation({
    mutationFn: () => {
      const b = { reason: reason.trim() };
      if (move === 'return') return api.threads.returnToTier1(t.id, b);
      return reseller ? api.partner.threads.escalate(t.id, b) : api.threads.escalate(t.id, b);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.threads });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const r = reason.trim();
    const p =
      move === 'escalate' && !r
        ? 'Say what Rethink needs to know.'
        : charCount(r) > MAX_REASON
          ? 'Keep it under 2,000 characters.'
          : undefined;
    setProblem(p);
    if (!p && !run.isPending) run.mutate();
  };
  return (
    <Modal
      opened
      onClose={onClose}
      title={move === 'escalate' ? 'Escalate to Rethink' : 'Return to reseller'}
    >
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <p style={{ margin: 0, fontSize: 14 }}>
          {move === 'escalate'
            ? 'Rethink support (tier 2) takes the conversation. You keep seeing it and can still reply.'
            : 'The customer’s reseller works the conversation again (tier 1).'}
        </p>
        <Textarea
          label={move === 'escalate' ? 'What have you tried, and what do you need?' : 'Note'}
          description="An internal note; the customer does not see it."
          autosize
          minRows={3}
          data-autofocus
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          error={problem}
        />
        {run.error && (
          <Alert color="red">
            {errorMessage(run.error, {
              already_tier2: 'Rethink already has this conversation.',
              already_tier1: 'The reseller already has this conversation.',
              no_reseller: 'This customer has no reseller to hand it to.',
              stale_tier: 'Someone moved this conversation meanwhile; reload it.',
            })}
          </Alert>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="dark" loading={run.isPending}>
            {move === 'escalate' ? 'Escalate' : 'Return'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
